import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  PracticesPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
} from "./PracticesPage.styles";
import Sidebar from "components/Sidebar";
import Table from "components/Table";
import IconButton from "components/Buttons/IconButton";
import LessonsButton from "components/Buttons/LessonsButton";
import { Practice } from "services/documentService/documentService.types";
import {
  fetchPractices,
  createPractice,
  updatePractice,
  deletePractice,
} from "services/documentService/documentService";
import Button from "components/Buttons/Button";
import { ButtonWrapper } from "../TemplatesPage.styles";
import { ConfirmModal, CreateModal } from "components/Modals";
import DeletedRow from "components/DeletedRow";
import RestoredRow from "components/RestoredRow";
import BreadcrumbLink from "components/BreadcrumbLink/BreadcrumbLink";
import { ButtonsContainer } from "../../DocumentVersionsPage/DocumentVersionsPage.styles";
import { formatServerErrors } from "utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { delIcon, editIcon } from "assets/icons";
const PracticesPage: React.FC = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [practiceToEdit, setPracticeToEdit] = useState<Practice | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useNotify();

  // Состояния для подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [practiceToDelete, setPracticeToDelete] = useState<Practice | null>(null);

  // Состояния для реализации отложенного удаления и уведомлений
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [searchParams] = useSearchParams();

  const documentName = searchParams.get("documentName") || "Неизвестный документ";
  const templateName = searchParams.get("templateName") || "Неизвестный шаблон";
  const { documentId } = useParams<{ documentId: string }>();
  const templateId = searchParams.get("templateId") || "0";

  const {
    items: practices,
    setItems: setPractices,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadPractices,
  } = useListPage<Practice, Awaited<ReturnType<typeof fetchPractices>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchPractices(Number(documentId), page, size, orderBy),
    select: (response) => ({ items: response.results, total: response.count }),
    errorMessage: "Ошибка загрузки практик",
  });

  useEffect(() => {
    if (!templateId) {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Необходимо указать документ.",
      });
      navigate("/login");
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);



  const openDeleteModal = (practice: Practice) => {
    setPracticeToDelete(practice);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setPracticeToDelete(null);
  };

  const confirmDelete = () => {
    if (practiceToDelete) {
      handleDelete(practiceToDelete.id);
      closeDeleteModal();
    }
  };

  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deletePractice(id);
        setPendingDelete(null);
        toast.show({
          severity: "success",
          summary: "Удалено",
          detail: "Практика успешно удалена",
          life: 2000,
        });
        loadPractices();
      } catch (error) {
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить практику",
          life: 3000,
        });
        logger.error("Ошибка при удалении практики:", error);
        setPendingDelete(null);
      }
    }, 5000);
  };

  // Функция восстановления: устанавливаем recentlyRestored и сбрасываем pendingDelete
  const restorePracticeHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete; // восстанавливаем тот, который ожидал удаления
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление практики отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const openCreateModal = () => {
    setPracticeToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (practice: Practice) => {
    setPracticeToEdit(practice);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateOrEditPractice = async (formData: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      if (practiceToEdit) {
        await updatePractice(practiceToEdit.id, {
          name: formData["name"] as string,
          content: formData["content"] as string,
          version: documentId 
        });
        setRecentlyEdited(practiceToEdit.id);
        toast.show({
          severity: "success",
          summary: "Успех",
          detail: "Практика обновлена",
          life: 3000,
        });
      } else {
        const createdPractice = await createPractice(Number(documentId), {
          name: formData["name"] as string,
          content: formData["content"] as string,
          version: documentId 
        });
        setRecentlyCreated(createdPractice.id);
        toast.show({
          severity: "success",
          summary: "Успех",
          detail: "Практика создана",
          life: 3000,
        });
      }
      closeCreateModal();
      loadPractices();
      setTimeout(() => {
        setRecentlyCreated(null);
        setRecentlyEdited(null);
      }, 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка при сохранении практики:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка сохранения практики",
          life: 3000,
        });
      }
    }
  };

  const headers = [
    { label: "Название практики", key: "name", fieldName: "name" },
    { label: "Содержание", key: "view", sortable: false },
    { label: "", key: "buttons", sortable: false, fieldName: "delete" },
  ];

  const rows = practices.map((practice) => {
    if (practice.id === pendingDelete) {
      return {
        name: (
          <DeletedRow
            id={practice.id}
            onRestore={restorePracticeHandler}
            colSpan={headers.length}
            message={`Практика "${practice.name}" будет удалена через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      name: practice.name,
      view: (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "300px",
          }}
          title={practice.content}
        >
          {practice.content}
        </div>
      ),
      buttons: (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(practice)}
          />
          <IconButton
            icon={delIcon}
            tone="danger"
            altText="Удалить"
            onClick={() => openDeleteModal(practice)}
          />
        </ButtonsContainer>
      ),
    };
  });

  // Вставляем строки уведомлений, как в CoursesPage
  if (recentlyRestored !== null) {
    const restoredIndex = practices.findIndex((p) => p.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Практика успешно восстановлена" />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const createdIndex = practices.findIndex((p) => p.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Практика успешно создана" />
        ),
      });
    }
  }
  if (recentlyEdited !== null) {
    const editedIndex = practices.findIndex((p) => p.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Практика успешно отредактирована" />
        ),
      });
    }
  }

  const rowAmountOptions = [
    { label: "7 записей", value: 7 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  return (
    <PracticesPageContainer>
      <Sidebar />
      <ContentWrapper>
        {/* Хлебные крошки */}
        <div style={{ marginBottom: "16px" }}>
          <BreadcrumbLink href="/documents" text="Шаблоны документов" />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/documents/${templateId}/versions?templateName=${templateName}`}
            text={`Шаблон ${templateName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/versions/${documentId}/practices?documentName=${documentName}&templateId=${templateId}}&templateName=${templateName}`}
            text={`Практические документа ${documentName}`}
          />
        </div>
        <HeaderContainer>
          <Title>Практики</Title>
          <ButtonWrapper>
            <Button onClick={openCreateModal} variant="primary" size="lg">
              Добавить практику
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          emptyState={{
            title: "Практик пока нет",
            description:
              "Практические задания шаблона появятся в этом списке.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          onSort={handleSort}
          specialRowKey="name"
        />
      </ContentWrapper>
      {isCreateModalOpen && (
        <CreateModal
          title={practiceToEdit ? "Редактировать практику" : "Новая практика"}
          fields={[
            { name: "name", type: "text", placeholder: "Название практики", required: true },
            { name: "content", type: "textarea", placeholder: "Содержание практики", required: true },
          ]}
          onSubmit={handleCreateOrEditPractice}
          submitButtonText={practiceToEdit ? "Сохранить" : "Создать"}
          onCancel={closeCreateModal}
          initialValues={practiceToEdit ? { name: practiceToEdit.name, content: practiceToEdit.content } : {}}
          externalErrors={externalErrors}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить практику "${practiceToDelete?.name}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}
    </PracticesPageContainer>
  );
};

export default PracticesPage;
